import React, { useMemo } from "react";
import LeftBar from "./LeftBar/LeftBar";
import RightBar from "./RightBar/RightBar";
import TopBar from "./TopBar/TopBar";
import * as AlfredoStyles from "../styles/layouts/layouts";
import {
  useCombinedStore,
  useLeftSidebarStore,
  useRightSidebarStore,
} from "../store";
import AlfredoCentral from "../components/AlfredoCentral";
import MessageBox from "../components/MessageBox";
import AlfredoIcon from "../assets/alfredo-central-icon.png";
import * as AlfredoInterfaces from "../interfaces";
import jwtDecode from "jwt-decode";

const MainLayout: React.FC<AlfredoInterfaces.MainLayoutProps> = ({
  children,
}) => {
  const { isLeftSidebarVisible, toggleLeftSidebar } = useLeftSidebarStore();
  const { isRightSidebarVisible, toggleRightSidebar } = useRightSidebarStore();
  const { authUserZustand, organizationZustand } = useCombinedStore();

  // Solução direta sem hook customizado
  const userInfo = useMemo(() => {
    if (!authUserZustand?.Token) return { userLogoUrl: "", userName: "" };

    try {
      const decryptedToken: any = jwtDecode(authUserZustand.Token);
      const logoUrl = decryptedToken?.userInfo?.userLogoUrl;

      return {
        userLogoUrl: logoUrl,
        userName: decryptedToken?.userInfo?.userName || "",
      };
    } catch (error) {
      console.error("Erro ao decodificar token:", error);
      return { userLogoUrl: "", userName: "" };
    }
  }, [authUserZustand?.Token]);

  return (
    <AlfredoStyles.LayoutContainer>
      <AlfredoStyles.TopBarContainer>
        <TopBar
          onLeftBarClick={toggleLeftSidebar}
          onRightBarClick={toggleRightSidebar}
          userInfo={userInfo}
          organization={organizationZustand}
        />
      </AlfredoStyles.TopBarContainer>
      <AlfredoStyles.CurveOverlayLeft isOpen={isLeftSidebarVisible} />
      <AlfredoStyles.CurveOverlayRight isOpen={isRightSidebarVisible} />
      <AlfredoStyles.MainContent
        leftbarVisible={isLeftSidebarVisible}
        rightbarVisible={isRightSidebarVisible}
      >
        <LeftBar isOpen={isLeftSidebarVisible} />

        <AlfredoStyles.ContentWrapper>
          {/* O aviso da casa só existia em LoginPage e RegisterPage. Toda
              mensagem que a área privada emitia — "salvou", "não salvou", "o
              PDV recusou", "nada mudou" — era gravada na store e nunca pintada.
              Sem isto, sucesso, erro e recusa somem exatamente iguais, e nada
              da Configuração é verificável na tela. */}
          {/* MODO DEMONSTRAÇÃO. Guardada por `REACT_APP_DEMO`, que o CRA lê em
              tempo de BUILD: a env não existe no serviço `alfredo-central` (o
              de produção), então lá este bloco é `false` e o bundle não muda.
              Ver o comentário de `FaixaDeDemonstracao` para por que ela é
              sticky dentro do conteúdo e não fixed no topo da janela. */}
          {process.env.REACT_APP_DEMO === "sim" && (
            <AlfredoStyles.FaixaDeDemonstracao>
              MODO DEMONSTRAÇÃO — dados fictícios. Nenhum pedido, cobrança ou
              cliente aqui é real.
            </AlfredoStyles.FaixaDeDemonstracao>
          )}
          <MessageBox />
          {children}
        </AlfredoStyles.ContentWrapper>

        <RightBar isOpen={isRightSidebarVisible} />

        {/* O FUNDO DA GAVETA. Só aparece abaixo de 1280 (o CSS decide), que é
            onde as barras passam a flutuar por cima do conteúdo em vez de
            empurrá-lo. Sem ele, com as duas amarelas, não dá para saber onde
            acaba a barra e começa a tela — e não há como fechar a barra sem
            procurar o botão do topo. */}
        <AlfredoStyles.FundoDaGaveta
          $lado="esquerda"
          $aberto={isLeftSidebarVisible}
          role="button"
          tabIndex={isLeftSidebarVisible ? 0 : -1}
          aria-label="Fechar o menu"
          onClick={toggleLeftSidebar}
        />
        <AlfredoStyles.FundoDaGaveta
          $lado="direita"
          $aberto={isRightSidebarVisible}
          role="button"
          tabIndex={isRightSidebarVisible ? 0 : -1}
          aria-label="Fechar o painel da loja"
          onClick={toggleRightSidebar}
        />
      </AlfredoStyles.MainContent>

      <AlfredoStyles.AlfredoCentralContainer
        leftbarVisible={isLeftSidebarVisible}
      >
        <AlfredoCentral onClick={toggleLeftSidebar} imgSrc={AlfredoIcon} />
      </AlfredoStyles.AlfredoCentralContainer>
    </AlfredoStyles.LayoutContainer>
  );
};

export default MainLayout;
